import React, { useCallback, useState, type ReactElement } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useLang } from '~/src/polyglot';

import { ForgotPassword } from './ForgotPassword.view';

import type { ForgotPasswordFormValues } from './ForgotPassword.types';

// Backend result codes (proto enum ForgotPasswordResult) mapped to local messages;
// its plain `message` stays the fallback for codes we don't know yet.
const RESULT_MESSAGES = {
  FORGOT_PASSWORD_RESULT_EMAIL_SENT: 'FORGOT_PASSWORD.RESULT.EMAIL_SENT',
  FORGOT_PASSWORD_RESULT_ATTEMPTS_EXCEEDED: 'FORGOT_PASSWORD.RESULT.ATTEMPTS_EXCEEDED',
} as const;

type ForgotPasswordResult = keyof typeof RESULT_MESSAGES;

export function ForgotPasswordWithSSO(): ReactElement {
  const [searchParams] = useSearchParams();
  const [errorMessage, setErrorMessage] = useState('');
  const [resultMessage, setResultMessage] = useState('');
  const lang = useLang();

  const sendTokenHandler = useCallback(
    ({ resetEmail }: ForgotPasswordFormValues) => fetch(
      '/api/v1/accounts/forgotPassword',
      {
        method: 'POST',
        body: JSON.stringify({ email: resetEmail }),
      },
    )
      .then(async (r) => {
        const { message, result } = await r.json() as { message: string, result?: string };

        if (r.status === 200) {
          const messageKey = RESULT_MESSAGES[result as ForgotPasswordResult];

          setResultMessage(messageKey ? lang({ message: messageKey }) : message);
        } else {
          throw new Error(message);
        }
      })
      .catch((error) => setErrorMessage(error.message)),
    [lang],
  );

  return (
    <ForgotPassword
      errorMessage={errorMessage}
      initialEmail={searchParams.get('email') || ''}
      sendTokenHandler={sendTokenHandler}
      resultMessage={resultMessage}
    />
  );
}
